import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { color } from '@trivie/ui-web/theme'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { BadgeAvatar } from '@trivie/ui-web/components/badge-avatar'
import { Insight } from '@trivie/ui-web/components/insight'
import { Text } from '@trivie/ui-web/components/text'
import { Punchcard } from '@trivie/ui-web/components/punchcard'
import { MessageDialog } from '@trivie/ui-web/components/message-dialog'
import { Meter } from '@trivie/ui-web/components/meter'
import { AddTags } from '@trivie/ui-web/components/add-tags'
import { AddGroups } from '@trivie/ui-web/components/add-groups'
import { DateFilter } from '@trivie/ui-web/components/date-filter'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { StrongestWeakest } from '@trivie/ui-web/components/strongest-weakest'
import { Box, CircularProgress, Divider, MenuItem, Paper, Select, Stack } from '@mui/material'
import { Sms, KeyboardArrowDownRounded, AddRounded } from '@mui/icons-material'
import {
  useStores,
  elapsedTime,
  numberWithOrdinalSuffix,
  TopicPctCorrectSummary,
  QuestionPctCorrectSummary,
  UserRole,
  getDisplayNumber,
  getStrongestWeakest,
} from '@trivie/core'
import { DateTime } from 'luxon'
import { useRouteMatch } from 'react-router-dom'
import { UserApi } from '@trivie/core/src/services/api-objects/UserApi'
import { Instance } from 'mobx-state-tree'
import { Medallion } from '@trivie/ui-web/components/medallion'
import { Medal } from '@trivie/ui-web/components/medal'
import { DropdownMenu } from '@trivie/ui-web/components/table-cells'
import { ShortList } from '@trivie/ui-web/components/short-list'
import { Button } from '@trivie/ui-web/components/button'
import { Switch } from '@trivie/ui-web/components/switch/switch'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

const Overview = observer(() => {
  const { manageUserStore, tagStore } = useStores()
  const {
    selectedUserProfile,
    getSelectedUserProfile,
    setSelectedUserId,
    levelAndPoints,
    overviewStats,
    generalStats,
    tags,
    groups,
    hourlyActivity,
    refreshOverviewData,
    toggleExcludeFromLeaderboard,
    getTags,
    clearOverviewData,
    getCertsMedalsForUser,
    userCertsMedals,
    permissions,
    getRoles,
    roles,
    userProfile,
    updateRoles,
    setSelectedUserProfile,
    getUserOverviewStats,
  } = manageUserStore

  const { removeUsersFromTags } = tagStore

  const [showSendMessage, setShowSendMessage] = useState(false)
  const [showAddTags, setShowAddTags] = useState(false)
  const [showAddGroups, setShowAddGroups] = useState(false)
  const [dateLabel, setDateLabel] = useState('Daily')
  const [start, setStart] = useState<any>(DateTime.local(1971).startOf('day').toISO())
  const [end, setEnd] = useState<any>(DateTime.local().endOf('day').toISO())
  const [topicGaps, setTopicGaps] = useState<null | Array<Instance<typeof TopicPctCorrectSummary>>>(
    null,
  )
  const [questionGaps, setQuestionGaps] = useState<null | Array<
    Instance<typeof QuestionPctCorrectSummary>
  >>(null)

  const match = useRouteMatch<any>('/people/users/:id/overview')

  const confirm = useConfirm()

  useEffect(() => {
    clearOverviewData()
    if (permissions?.permissions.some((p) => p.code === 'can_edit_workspace_settings')) {
      getRoles(true)
    }
    if (match?.params.id) {
      UserApi.getUserTopicGaps(match.params.id).then((response) => {
        if (response.ok && response.data) {
          setTopicGaps(response.data.topics)
        }
      })
      UserApi.getUserQuestionGaps(match.params.id).then((response) => {
        if (response.ok && response.data) {
          setQuestionGaps(response.data.questions)
        }
      })

      getCertsMedalsForUser(match?.params.id)
    }
    return () => {
      clearOverviewData()
    }
  }, [])

  useEffect(() => {
    setSelectedUserId(Number(match?.params.id))
  }, [match?.params.id])

  useEffect(() => {
    setSelectedUserProfile(null)
    getSelectedUserProfile(match?.params.id)
  }, [match?.params.id])

  const handleToggleLeaderboard = (value: boolean) => {
    toggleExcludeFromLeaderboard(value)
  }

  const refresh = (startDateTime: any, endDateTime: any, label: any) => {
    if (label) {
      setDateLabel(label)
    }
    setStart(startDateTime)
    setEnd(endDateTime)
    refreshOverviewData(startDateTime, endDateTime, match?.params.id)
  }

  const getFileName = () => {
    return (
      (selectedUserProfile?.first_name ?? '').concat(selectedUserProfile?.last_name ?? '') ||
      'user-detail'
    )
  }

  const renderUserCerts = (level: string) => {
    const certs = userCertsMedals?.certifications.filter((cert) => cert.level === level)
    return (
      <>
        {certs && certs.length > 0 && (
          <Text
            text={`${level} Certified`}
            variant="body1Emphasized"
            my={1}
            left="20px"
            position="relative"
          />
        )}
        {certs?.map((cert) => {
          return (
            <Stack
              maxHeight={48}
              position="relative"
              left="-12px"
              alignItems="center"
              direction="row"
            >
              <Medallion scale={0.37} recall={cert.recall} type={cert.level} />
              <Text position="relative" left="-20px" variant="body1Emphasized" text={cert.title} />
            </Stack>
          )
        })}
      </>
    )
  }

  const renderUserMedals = (level: string) => {
    const medals = userCertsMedals?.medals.filter((cert) => cert.level === level)

    return (
      <>
        {medals && medals.length > 0 && (
          <Text text={`${level} Medals`} variant="body1Emphasized" position="relative" />
        )}
        {medals?.map((medal) => {
          return (
            <Stack
              useFlexGap
              gap={2}
              maxHeight={48}
              position="relative"
              alignItems="center"
              direction="row"
            >
              <Medal size={32} recall={medal.recall} level={medal.level} />
              <Text variant="body1Emphasized" text={medal.title} />
            </Stack>
          )
        })}
      </>
    )
  }

  const [anchorEl, setAnchorEl] = React.useState(null)

  const [selectedRole, setSelectedRole] = useState<any>()

  useEffect(() => {
    if (overviewStats?.roles) {
      const selected = overviewStats?.roles.slice().sort((a: any, b: any) => a.order - b.order)[0]
      setSelectedRole(selected)
    }
  }, [overviewStats?.roles])

  const onExpandRole = (event: any) => setAnchorEl(event.currentTarget)

  const handleCloseRole = () => setAnchorEl(null)

  const onChangeRole = (role: Instance<typeof UserRole>) => {
    handleCloseRole()
    if (match?.params.id) {
      updateRoles(match?.params.id, [role?.id]).then(() => {
        getUserOverviewStats(start, end, match?.params.id).then(() => {
          const selected = overviewStats?.roles
            .slice()
            .sort((a: any, b: any) => a.order - b.order)[0]
          setSelectedRole(selected)
        })
      })
    }
  }

  const gdpr_enabled = userProfile?.workspace_settings?.gdpr_enabled

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {!gdpr_enabled && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <DateFilter
              onDateChange={(startDateTime, endDateTime, label) =>
                refresh(startDateTime, endDateTime, label)
              }
            />
            <GenerateReport filename={getFileName()} />
          </div>
        )}

        {!overviewStats || !selectedUserProfile ? (
          <div style={{ margin: 'auto' }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ display: 'flex', marginBottom: 20 }}>
            <div style={{ marginRight: 16 }}>
              <Paper
                variant="outlined"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 320,
                  overflow: 'hidden',
                }}
              >
                <div style={{ position: 'relative', width: '100%' }}>
                  <div
                    style={{
                      height: '220px',
                      width: '100%',
                      position: 'absolute',
                      backgroundColor: color.shade70,
                      backgroundImage: `url(${selectedUserProfile?.photo_url ?? ''})`,
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  />
                  <div
                    style={{
                      height: selectedUserProfile?.phone_number ? '236px' : '230px',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(0,0,0,.1)',
                      backdropFilter: 'blur(50px)',
                    }}
                  >
                    <Avatar
                      name={
                        selectedUserProfile?.first_name && selectedUserProfile?.last_name
                          ? `${selectedUserProfile?.first_name} ${selectedUserProfile?.last_name}`
                          : ''
                      }
                      url={selectedUserProfile?.photo_url}
                      size={90}
                      fontStyle={{ fontSize: 32 }}
                    />
                    {selectedUserProfile?.first_name && selectedUserProfile?.first_name ? (
                      <Text
                        variant="headline"
                        text={`${selectedUserProfile?.first_name} ${selectedUserProfile?.last_name}`}
                        style={{
                          zIndex: 300,
                          color: color.white,
                          marginTop: 15,
                          marginBottom: 8,
                        }}
                      />
                    ) : selectedUserProfile?.email ? (
                      <Text
                        variant="headline"
                        text={`${selectedUserProfile?.email}`}
                        style={{
                          zIndex: 300,
                          color: color.white,
                          marginTop: 15,
                          marginBottom: 8,
                        }}
                      />
                    ) : null}
                    {selectedUserProfile?.first_name && selectedUserProfile?.first_name ? (
                      <div
                        style={{
                          marginBottom: selectedUserProfile?.phone_number ? 16 : 0,
                          zIndex: 300,
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {selectedUserProfile?.email && (
                          <Text
                            variant="body1Medium"
                            text={selectedUserProfile?.email}
                            style={{
                              zIndex: 300,
                              color: !selectedUserProfile?.photo_url
                                ? color.white50
                                : 'rgba(255,255,255,.5)',
                              marginBottom: 8,
                            }}
                          />
                        )}
                        {selectedUserProfile?.phone_number && (
                          <Text
                            variant="body1Medium"
                            text={getDisplayNumber(selectedUserProfile?.phone_number)}
                            style={{
                              zIndex: 300,
                              color: !selectedUserProfile?.photo_url
                                ? color.white70
                                : 'rgba(255,255,255,.5)',
                            }}
                          />
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flex: 1,
                    paddingTop: 32,
                    paddingBottom: 32,
                  }}
                >
                  {!gdpr_enabled && (
                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                      <Text
                        variant="body1Medium"
                        text={
                          selectedUserProfile?.last_activity
                            ? `Last active ${elapsedTime(selectedUserProfile.last_activity, 'long')}`
                            : 'Last active N/A'
                        }
                        style={{ color: color.shade50 }}
                      />
                    </div>
                  )}
                  <Button
                    disabled={
                      overviewStats?.deactivation_datetime ||
                      !permissions?.permissions.some((p) => p.code === 'can_message_user')
                    }
                    variant="outlined"
                    color="info"
                    text="Message"
                    startIcon={<Sms />}
                    onClick={() => setShowSendMessage(true)}
                  />
                </div>
              </Paper>
              <Panel sx={{ mt: 2 }} title="User Settings" divider>
                <Stack direction="row">
                  <Stack justifyContent="center" direction="column" flex={2}>
                    <Text variant="body1Medium" text="Status" />
                    <Text
                      variant="body2Medium"
                      text="Set user status"
                      style={{ color: color.shade50 }}
                    />
                  </Stack>
                  <Select
                    disabled={!permissions?.permissions.some((p) => p.code === 'can_edit_user')}
                    style={{ width: '150px', height: 41 }}
                    IconComponent={KeyboardArrowDownRounded}
                    value={overviewStats?.deactivation_datetime ? 'Deactivated' : 'Active'}
                    onChange={(e) => {
                      const active = String(e.target.value) === 'Active'
                      confirm({
                        danger: true,
                        title: active ? 'Reactivate User' : 'Deactivate User',
                        confirmationMessage: `${selectedUserProfile.first_name} ${selectedUserProfile.last_name} has been ${active ? 'reactivated' : 'deactivated'}.`,
                        dialogText: `Are you sure you want to ${active ? 'reactivate' : 'deactivate'} ${selectedUserProfile.first_name} ${selectedUserProfile.last_name}?`,
                      })
                        .then(() => {
                          manageUserStore
                            .changeStatus(active ? 'activate' : 'deactivate', [
                              selectedUserProfile.id,
                            ])
                            .then(() => {
                              refresh(start, end, dateLabel)
                            })
                        })
                        .catch(() => {})
                    }}
                  >
                    <MenuItem value={'Active'}>Active</MenuItem>
                    <MenuItem value={'Deactivated'}>Deactivated</MenuItem>
                  </Select>
                </Stack>
                {permissions?.permissions.some(
                  (p) => p.code === 'can_assign_permissions_to_user',
                ) && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Text variant="body1Medium" text="Role" />
                      <Text
                        variant="body2Medium"
                        text="Set user role"
                        style={{ color: color.shade50 }}
                      />
                    </div>
                    <div
                      style={{
                        width: 150,
                        height: 41,
                        border: '1px solid rgba(0,0,0,0.05)',
                        borderRadius: 4,
                        padding: 12,
                        paddingRight: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        display: 'flex',
                        cursor:
                          selectedRole?.name === 'Owner' ||
                          !permissions?.permissions.some(
                            (p) => p.code === 'can_edit_workspace_settings',
                          ) ||
                          match?.params.id === userProfile?.id
                            ? 'default'
                            : 'pointer',
                      }}
                      onClick={
                        selectedRole?.name === 'Owner' ||
                        match?.params.id === userProfile?.id ||
                        !permissions?.permissions.some(
                          (p) => p.code === 'can_edit_workspace_settings',
                        )
                          ? () => {}
                          : onExpandRole
                      }
                    >
                      <Text
                        style={{
                          color:
                            selectedRole?.name === 'Owner' ||
                            !permissions?.permissions.some(
                              (p) => p.code === 'can_edit_workspace_settings',
                            ) ||
                            match?.params.id === userProfile?.id
                              ? color.shade30
                              : color.black,
                        }}
                        variant="body1"
                        text={selectedRole?.name}
                      />
                      <KeyboardArrowDownRounded style={{ color: 'rgba(0,0,0,0.54)' }} />
                    </div>
                    <DropdownMenu
                      selected={selectedRole}
                      items={roles.slice()}
                      onChange={onChangeRole}
                      handleClose={handleCloseRole}
                      anchorEl={anchorEl}
                    />
                  </div>
                )}

                {!gdpr_enabled && (
                  <Stack useFlexGap gap={1} justifyContent="space-between" direction="row">
                    <Stack>
                      <Text variant="body1Medium" text="Show in Leaderboard" />
                      <Text
                        variant="body2Medium"
                        text={`Display ${selectedUserProfile?.first_name} in user leaderboards.`}
                        style={{ color: color.shade50 }}
                      />
                    </Stack>
                    <Switch
                      disabled={!permissions?.permissions.some((p) => p.code === 'can_edit_user')}
                      color="success"
                      onChange={(e) => handleToggleLeaderboard(e.target.checked)}
                      checked={!selectedUserProfile?.exclude_from_leaderboard}
                    />
                  </Stack>
                )}
              </Panel>
              {!gdpr_enabled &&
                userCertsMedals?.certifications &&
                userCertsMedals?.certifications.length > 0 && (
                  <Panel sx={{ mt: 2 }} divider title="Certifications">
                    <Stack maxHeight="500px" overflow="auto" left="-20px" position="relative">
                      {renderUserCerts('Gold')}
                      {renderUserCerts('Silver')}
                      {renderUserCerts('Bronze')}
                    </Stack>
                  </Panel>
                )}
              {!gdpr_enabled && userCertsMedals?.medals && userCertsMedals?.medals.length > 0 && (
                <Panel sx={{ mt: 2 }} divider title="Medals">
                  <Stack useFlexGap gap={2} maxHeight="500px" overflow="auto">
                    {renderUserMedals('Gold')}
                    {renderUserMedals('Silver')}
                    {renderUserMedals('Bronze')}
                  </Stack>
                </Panel>
              )}

              {!gdpr_enabled && (
                <Panel sx={{ mt: 2 }} title="Points" divider>
                  <Text variant="body1Emphasized" text="Levels" style={{ color: color.shade50 }} />
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <BadgeAvatar
                      size={36}
                      name={levelAndPoints?.user_level}
                      url={levelAndPoints?.level_icon}
                      hoverable={false}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>
                      <Text variant="titleEmphasized" text={levelAndPoints?.label} />
                      <div style={{ display: 'flex', marginTop: 5 }}>
                        <Text
                          variant="body2Emphasized"
                          text={`${levelAndPoints?.points.total_points ?? 0} `}
                        />
                        {levelAndPoints?.level_max_points && (
                          <Text
                            variant="body2Emphasized"
                            text={` / ${levelAndPoints?.level_max_points}`}
                            style={{ color: color.shade50 }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <Box mb={2}>
                    <Meter
                      variant={'green'}
                      value={Math.round(
                        (levelAndPoints?.points.total_points / levelAndPoints?.level_max_points) *
                          100,
                      )}
                      style={{ height: '6px', flex: 1 }}
                    />
                  </Box>
                  <Stack useFlexGap gap={2}>
                    <Text
                      variant="body1Emphasized"
                      text="Total Score"
                      style={{ color: color.shade50 }}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.total_points ?? 0} Total Points`}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.answer_points ?? 0} Answer Points`}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.post_points ?? 0} Post Points`}
                      style={{ color: color.shade50 }}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.reply_points ?? 0} Reply Points`}
                      style={{ color: color.shade50 }}
                      mb={2}
                    />
                    <Text
                      variant="body1Emphasized"
                      text={dateLabel}
                      style={{ color: color.shade50 }}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.period_total_points ?? 0} Total Points`}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.period_answer_points ?? 0} Answer Points`}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.period_post_points ?? 0} Post Points`}
                      style={{ color: color.shade50 }}
                    />
                    <Text
                      variant="titleMedium"
                      text={`${levelAndPoints?.points.period_reply_points ?? 0} Reply Points`}
                      style={{ color: color.shade50 }}
                    />
                    <Divider style={{ marginBottom: 16, marginTop: 16 }} />
                    <Text
                      variant="body1Emphasized"
                      text="Leaderboard"
                      style={{ color: color.shade50 }}
                    />
                    <Text
                      variant="body1Emphasized"
                      text={`${numberWithOrdinalSuffix(levelAndPoints?.current_rank ?? 0)} Place`}
                      style={{ color: color.palette.blueLink, whiteSpace: 'nowrap' }}
                    />
                  </Stack>
                </Panel>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              {gdpr_enabled && selectedUserProfile && (
                <Paper
                  style={{
                    display: 'flex',
                    backgroundColor: color.white,
                    flexDirection: 'row',
                    padding: 24,
                    marginBottom: 16,
                    alignItems: 'center',
                  }}
                  variant="outlined"
                >
                  <svg
                    width="31"
                    height="31"
                    style={{ marginRight: 25 }}
                    viewBox="0 0 31 31"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.4379 31C12.4216 31 10.5374 30.6249 8.78514 29.8747C7.03291 29.1245 5.50271 28.096 4.19454 26.7892C2.88637 25.4824 1.86024 23.9457 1.11614 22.1792C0.372048 20.4126 0 18.5129 0 16.4801C0 14.4473 0.372048 12.5476 1.11614 10.781C1.86024 9.01444 2.87437 7.47775 4.15854 6.17096C5.4427 4.86417 6.9429 3.83568 8.65912 3.08548C10.3753 2.33528 12.2056 1.96019 14.1498 1.96019C15.23 1.96019 16.3221 2.06304 17.4263 2.26874C18.5304 2.47443 19.3705 2.71038 19.9466 2.97658V5.59016C19.9466 6.41296 19.6285 7.145 18.9925 7.7863C18.3564 8.4276 17.6303 8.74824 16.8142 8.74824H12.7096V11.6522C12.7096 12.112 12.5476 12.5053 12.2236 12.832C11.8995 13.1587 11.5095 13.322 11.0534 13.322H8.17306V16.4801H17.2822C17.7623 16.4801 18.1643 16.6434 18.4884 16.9701C18.8124 17.2968 18.9744 17.7022 18.9744 18.1862V22.7963H20.4146C21.1107 22.7963 21.7288 22.9899 22.2689 23.3771C22.8089 23.7642 23.175 24.2724 23.367 24.9016C24.4472 23.6674 25.2633 22.3425 25.8153 20.9268C26.3674 19.5111 26.6434 18.0773 26.6434 16.6253C26.6434 16.2623 26.6314 15.8206 26.6074 15.3004C26.5834 14.7801 26.5474 14.3626 26.4994 14.048H28.6597C28.7077 14.29 28.7437 14.6409 28.7677 15.1007C28.7917 15.5605 28.8037 16.0082 28.8037 16.4438C28.8037 18.4766 28.4317 20.3823 27.6876 22.161C26.9435 23.9397 25.9233 25.4824 24.6272 26.7892C23.331 28.096 21.8128 29.1245 20.0726 29.8747C18.3324 30.6249 16.4541 31 14.4379 31ZM12.7096 29.0035V25.7002C11.9175 25.7002 11.2395 25.4159 10.6754 24.8472C10.1113 24.2785 9.82927 23.5948 9.82927 22.7963V21.3443L2.41231 13.8665C2.3163 14.4231 2.25029 14.8769 2.21429 15.2278C2.17828 15.5786 2.16028 15.9598 2.16028 16.3712C2.16028 19.614 3.15641 22.4212 5.14866 24.7927C7.14092 27.1643 9.66125 28.5679 12.7096 29.0035ZM23.187 11.87C22.875 11.87 22.6169 11.7672 22.4129 11.5615C22.2089 11.3558 22.1069 11.0956 22.1069 10.781V5.48126C22.1069 5.16667 22.2089 4.90652 22.4129 4.70082C22.6169 4.49512 22.875 4.39227 23.187 4.39227H23.7631V2.94028C23.7631 2.14169 24.0331 1.45199 24.5732 0.871194C25.1132 0.290398 25.7673 0 26.5354 0C27.3035 0 27.9576 0.290398 28.4977 0.871194C29.0377 1.45199 29.3078 2.14169 29.3078 2.94028V4.39227H29.9199C30.2319 4.39227 30.4899 4.49512 30.694 4.70082C30.898 4.90652 31 5.16667 31 5.48126V10.781C31 11.0956 30.898 11.3558 30.694 11.5615C30.4899 11.7672 30.2319 11.87 29.9199 11.87H23.187ZM24.9872 4.39227H28.0836V2.94028C28.0836 2.48048 27.9396 2.08119 27.6516 1.74239C27.3635 1.40359 26.9915 1.23419 26.5354 1.23419C26.0794 1.23419 25.7073 1.40359 25.4193 1.74239C25.1312 2.08119 24.9872 2.48048 24.9872 2.94028V4.39227Z"
                      fill="#1B59F8"
                    />
                  </svg>
                  <div style={{ flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Text style={{ marginBottom: 8 }} variant="labelEmphasized" text="GDPR: On" />
                    <Text
                      variant="body1"
                      text={`${selectedUserProfile?.first_name}'${
                        selectedUserProfile?.first_name &&
                        selectedUserProfile?.first_name[
                          selectedUserProfile?.first_name.length - 1
                        ] === 's'
                          ? ''
                          : 's'
                      } data is hidden`}
                    />
                  </div>
                </Paper>
              )}
              {!gdpr_enabled && (
                <>
                  <Stack direction="row" mb={2} gap={2}>
                    <Panel sx={{ flex: 1 }}>
                      <Insight
                        titleVariant="titleEmphasized"
                        timespan={dateLabel}
                        unit="%"
                        style={{ marginRight: 20 }}
                        data={overviewStats?.stats_history[2].data_points}
                        title="Baseline Knowledge"
                        loading={!overviewStats}
                        value={
                          overviewStats
                            ? `${
                                Number.isInteger(overviewStats?.starting_knowledge)
                                  ? overviewStats?.starting_knowledge
                                  : overviewStats?.starting_knowledge?.toFixed(1) ?? 0
                              }%`
                            : '0%'
                        }
                      />
                    </Panel>
                    <Panel sx={{ flex: 1 }}>
                      <Insight
                        titleVariant="titleEmphasized"
                        timespan={dateLabel}
                        unit="%"
                        style={{ marginRight: 20 }}
                        data={overviewStats?.stats_history[1].data_points}
                        title="Current Knowledge"
                        loading={!overviewStats}
                        value={
                          overviewStats
                            ? `${
                                Number.isInteger(overviewStats?.current_knowledge)
                                  ? overviewStats?.current_knowledge
                                  : overviewStats?.current_knowledge?.toFixed(1) ?? 0
                              }%`
                            : '0%'
                        }
                      />
                    </Panel>
                    <Panel sx={{ flex: 1 }}>
                      <Insight
                        titleVariant="titleEmphasized"
                        timespan={dateLabel}
                        unit="%"
                        data={overviewStats?.stats_history[0].data_points}
                        title="Knowledge Gain"
                        loading={!overviewStats}
                        value={
                          overviewStats
                            ? `${
                                Number.isInteger(overviewStats?.knowledge_gain)
                                  ? overviewStats?.knowledge_gain
                                  : overviewStats?.knowledge_gain?.toFixed(1) ?? 0
                              }%`
                            : '0%'
                        }
                      />
                    </Panel>
                  </Stack>
                  <Stack direction="row" mb={2} gap={2}>
                    <Panel sx={{ flex: 1 }}>
                      <Insight
                        titleVariant="titleEmphasized"
                        timespan={dateLabel}
                        data={generalStats?.stats_history[0]?.data_points}
                        title="Questions Answered"
                        unit=" Questions"
                        value={`${generalStats?.questions_answered ?? 0}`}
                        style={{ marginRight: 20 }}
                      />
                    </Panel>
                    <Panel sx={{ flex: 1 }}>
                      <Insight
                        titleVariant="titleEmphasized"
                        timespan={dateLabel}
                        tooltip="test"
                        data={generalStats?.stats_history[1]?.data_points}
                        title="Quizzes Completed"
                        unit=" Quizzes"
                        value={`${generalStats?.quizzes_completed ?? 0}`}
                        style={{ marginRight: 20 }}
                      />
                    </Panel>
                    <Panel sx={{ flex: 1 }}>
                      <Insight
                        titleVariant="titleEmphasized"
                        timespan={dateLabel}
                        title="Mastery"
                        unit=""
                        data={generalStats?.stats_history[2]?.data_points}
                        value={`${generalStats?.mastery?.toFixed(1) ?? 0}`}
                      />
                    </Panel>
                  </Stack>

                  <div style={{ display: 'flex', marginBottom: 20 }}>
                    {topicGaps || questionGaps ? (
                      <StrongestWeakest
                        type="Weakest"
                        variant="small"
                        topics={getStrongestWeakest(topicGaps ?? []).weakest}
                        questions={getStrongestWeakest(questionGaps ?? []).weakest}
                        style={{ flex: 1, marginRight: 20 }}
                      />
                    ) : null}
                    {topicGaps || questionGaps ? (
                      <StrongestWeakest
                        type="Strongest"
                        variant="small"
                        topics={getStrongestWeakest(topicGaps ?? []).strongest}
                        questions={getStrongestWeakest(questionGaps ?? []).strongest}
                        style={{ flex: 1 }}
                      />
                    ) : null}
                  </div>
                </>
              )}
              <Stack mb={2} direction="row" useFlexGap gap={2}>
                <ShortList
                  data={tags}
                  type="Tags"
                  action={
                    <Button
                      disabled={!permissions?.permissions.some((p) => p.code === 'can_edit_user')}
                      onClick={() => setShowAddTags(true)}
                      startIcon={<AddRounded />}
                      variant="outlined"
                      text="Add Tag"
                      color="info"
                    />
                  }
                  rowAction={(id) => {
                    removeUsersFromTags([match?.params.id], [id]).then(() => {
                      getSelectedUserProfile()
                      getTags()
                    })
                  }}
                />
                <ShortList
                  data={groups}
                  type="Groups"
                  action={
                    <Button
                      disabled={!permissions?.permissions.some((p) => p.code === 'can_edit_user')}
                      onClick={() => setShowAddGroups(true)}
                      startIcon={<AddRounded />}
                      text="Add Group"
                      variant="outlined"
                      color="info"
                    />
                  }
                />
              </Stack>

              {!gdpr_enabled && (
                <Panel divider title={`When is ${selectedUserProfile?.first_name} learning most?`}>
                  {hourlyActivity?.length ? (
                    <Punchcard values={hourlyActivity} />
                  ) : (
                    <Text variant="body2" text="no hourly data" />
                  )}
                </Panel>
              )}
            </div>
          </div>
        )}
      </div>

      {showSendMessage && match?.params.id && (
        <MessageDialog
          ids={[Number(match?.params.id)]}
          open={showSendMessage}
          messageType="generic"
          onClose={() => setShowSendMessage(false)}
        />
      )}

      {showAddTags && (
        <AddTags
          existing={tags ?? []}
          open={showAddTags}
          users={[match?.params.id]}
          onClose={() => setShowAddTags(false)}
          onConfirm={() => getTags()}
        />
      )}
      {showAddGroups && (
        <AddGroups
          open={showAddGroups}
          groups={groups ?? []}
          onClose={() => setShowAddGroups(false)}
        />
      )}
    </>
  )
})

export const UsersOverviewTab = (props: any) => {
  const { id } = props
  return {
    name: 'Overview',
    path: `/people/users/${id}/overview`,
    component: () => <Overview />,
  }
}
